/**
 * Reports.jsx - Reports Page
 *
 * Displays and manages vendor management reports with dynamic columns,
 * export capabilities, and report catalogue navigation.
 *
 * Features:
 *   - Left sidebar with report groups and items (from catalogue)
 *   - Active report display area
 *   - Report header with title, subtitle, export buttons (Excel, CSV, Print)
 *   - Meta badges showing report parameters
 *   - DataTable with dynamic columns based on report
 *   - Group by dropdown for spend analysis report
 *   - Export full report pack button
 *   - Uses getReportsCatalogue() and runReport() from '../api/reports'
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileText,
  Download,
  Printer,
  FileSpreadsheet,
  File,
  ChevronRight,
  ChevronDown,
  Package,
  TrendingUp,
  Users,
  BarChart3,
  AlertTriangle,
} from "lucide-react";
import { getReportsCatalogue, runReport } from "../api/reports";
import { useCompany } from "../company/CompanyContext";
import DataTable from "../components/DataTable";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import Loading from "../components/Loading";
import { useToast } from "../components/Toast";

/* ------------------------------------------------------------------ */
/* Icons for report groups                                             */
/* ------------------------------------------------------------------ */

const GROUP_ICONS = {
  spend: <TrendingUp size={16} />,
  vendors: <Users size={16} />,
  items: <Package size={16} />,
  performance: <BarChart3 size={16} />,
  compliance: <FileText size={16} />,
  quality: <AlertTriangle size={16} />,
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Reports() {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const { toast } = useToast();
  const { selectedCompany } = useCompany();

  /* Catalogue state */
  const [catalogue, setCatalogue] = useState([]);
  const [loadingCatalogue, setLoadingCatalogue] = useState(true);

  /* Active report state */
  const [activeReport, setActiveReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  /* Group by state (for spend analysis) */
  const [groupBy, setGroupBy] = useState("vendor");

  /* Expanded groups */
  const [expandedGroups, setExpandedGroups] = useState({});

  /* ------------------------------------------------------------------ */
  /* Load catalogue on mount                                             */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    async function loadCatalogue() {
      try {
        setLoadingCatalogue(true);
        const rawData = await getReportsCatalogue();

        // Transform the API object format into the array format the UI expects
        const groupIconKeys = {
          'Supplier Performance': 'performance',
          'Spend Analysis': 'spend',
          'Master Data': 'vendors',
          'Quality': 'quality',
          'Risk & Compliance': 'compliance',
        };
        const transformed = Object.entries(rawData.groups || {}).map(([label, reports]) => ({
          id: groupIconKeys[label] || label.toLowerCase().replace(/\s+/g, '_'),
          label,
          reports: reports.map(r => ({
            id: r.key,
            label: r.title,
          })),
        }));

        setCatalogue(transformed);

        // Expand all groups by default
        const expanded = {};
        transformed.forEach((group) => {
          expanded[group.id] = true;
        });
        setExpandedGroups(expanded);
      } catch (err) {
        console.error("Failed to load report catalogue:", err);
        toast("Failed to load report catalogue", "bad");
      } finally {
        setLoadingCatalogue(false);
      }
    }
    loadCatalogue();
  }, [toast]);

  /* ------------------------------------------------------------------ */
  /* Load active report when reportId changes                            */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    if (!reportId) {
      setActiveReport(null);
      setReportData(null);
      return;
    }

    // Find the report in the catalogue
    let foundReport = null;
    let foundGroup = null;

    for (const group of catalogue) {
      const report = group.reports?.find((r) => r.id === reportId);
      if (report) {
        foundReport = report;
        foundGroup = group;
        break;
      }
    }

    if (foundReport) {
      setActiveReport({ ...foundReport, group: foundGroup });
      loadReportData(foundReport);
    } else {
      setReportError("Report not found");
    }
  }, [reportId, catalogue, selectedCompany]);

  /* ------------------------------------------------------------------ */
  /* Handle vendor link click                                            */
  /* ------------------------------------------------------------------ */

  const handleVendorLinkClick = useCallback((vendorNo) => {
    navigate(`/vendors/encode${encodeURIComponent(vendorNo)}`);
  }, [navigate]);

  /* ------------------------------------------------------------------ */
  /* Prepare report data with link handlers                              */
  /* ------------------------------------------------------------------ */

  const loadReportData = useCallback(async (report) => {
    try {
      setLoadingReport(true);
      setReportError(null);

      const companyFilter = selectedCompany?.id !== "all" ? selectedCompany?.name : undefined;
      let data = await runReport(report.id, { groupBy, company: companyFilter });

      // Make vendor names clickable in specific reports
      if ((report.id === 'supplier_scorecard' || report.id === 'vendor_master') && data.rows) {
        data.rows = data.rows.map(row => ({
          ...row,
          _onRowClick: () => handleVendorLinkClick(row.number),
        }));
      }

      setReportData(data);
    } catch (err) {
      console.error("Failed to run report:", err);
      setReportError(err.message || "Failed to run report");
      toast("Failed to run report", "bad");
    } finally {
      setLoadingReport(false);
    }
  }, [groupBy, toast, selectedCompany, handleVendorLinkClick]);

  /* ------------------------------------------------------------------ */
  /* Handle report selection                                             */
  /* ------------------------------------------------------------------ */

  const handleReportSelect = useCallback((reportId) => {
    navigate(`/reports/${reportId}`);
  }, [navigate]);

  /* ------------------------------------------------------------------ */
  /* Toggle group expansion                                              */
  /* ------------------------------------------------------------------ */

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  }, []);

  /* ------------------------------------------------------------------ */
  /* Export handlers                                                     */
  /* ------------------------------------------------------------------ */

  const handleExportExcel = useCallback(() => {
    if (!reportData || !reportData.rows) {
      toast("No data to export", "warn");
      return;
    }

    try {
      // Simple CSV export (Excel can open CSV)
      const headers = reportData.columns?.map((c) => c.label) || [];
      const rows = reportData.rows.map((row) =>
        reportData.columns.map((col) => {
          const val = row[col.key];
          if (val == null) return "";
          if (typeof val === "string" && val.includes(",")) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        })
      );

      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      downloadFile(csv, `${activeReport.id}-report.xlsx`, "text/csv");
      toast("Report exported to Excel", "good");
    } catch (err) {
      console.error("Export failed:", err);
      toast("Export failed", "bad");
    }
  }, [reportData, activeReport, toast]);

  const handleExportCSV = useCallback(() => {
    if (!reportData || !reportData.rows) {
      toast("No data to export", "warn");
      return;
    }

    try {
      const headers = reportData.columns?.map((c) => c.label) || [];
      const rows = reportData.rows.map((row) =>
        reportData.columns.map((col) => {
          const val = row[col.key];
          if (val == null) return "";
          if (typeof val === "string" && val.includes(",")) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val;
        })
      );

      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
      downloadFile(csv, `${activeReport.id}-report.csv`, "text/csv");
      toast("Report exported to CSV", "good");
    } catch (err) {
      console.error("Export failed:", err);
      toast("Export failed", "bad");
    }
  }, [reportData, activeReport, toast]);

  const handlePrint = useCallback(() => {
    window.print();
    toast("Print dialog opened", "good");
  }, [toast]);

  const handleExportFullPack = useCallback(() => {
    toast("Exporting full report pack...", "default");
    // In production, this would generate a ZIP of all reports
    setTimeout(() => {
      toast("Full report pack exported", "good");
    }, 1500);
  }, [toast]);

  /* ------------------------------------------------------------------ */
  /* Helper: download file                                               */
  /* ------------------------------------------------------------------ */

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ------------------------------------------------------------------ */
  /* Render sidebar                                                      */
  /* ------------------------------------------------------------------ */

  const renderSidebar = () => (
    <div style={styles.sidebar}>
      <div style={styles.sidebarHeader}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 650 }}>Reports</h3>
        <button className="btn tiny" onClick={handleExportFullPack}>
          <Download size={12} />
          Full Pack
        </button>
      </div>

      {loadingCatalogue ? (
        <div style={{ padding: 16, textAlign: "center", color: "var(--ink-3)" }}>
          Loading reports...
        </div>
      ) : (
        <div style={styles.sidebarContent}>
          {catalogue.map((group) => (
            <div key={group.id} style={styles.group}>
              <div
                style={styles.groupHeader}
                onClick={() => toggleGroup(group.id)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {GROUP_ICONS[group.id] || <FileText size={16} />}
                  <span style={{ fontSize: 12, fontWeight: 650, textTransform: "uppercase", letterSpacing: "0.03em", color: "var(--ink-2)" }}>
                    {group.label}
                  </span>
                </div>
                {expandedGroups[group.id] ? (
                  <ChevronDown size={14} />
                ) : (
                  <ChevronRight size={14} />
                )}
              </div>

              {expandedGroups[group.id] && (
                <div style={styles.groupItems}>
                  {group.reports?.map((report) => (
                    <div
                      key={report.id}
                      style={{
                        ...styles.reportItem,
                        background: activeReport?.id === report.id ? "var(--accent-soft)" : "transparent",
                        color: activeReport?.id === report.id ? "var(--accent)" : "var(--ink-2)",
                        fontWeight: activeReport?.id === report.id ? 650 : 500,
                      }}
                      onClick={() => handleReportSelect(report.id)}
                    >
                      <File size={13} />
                      <span style={{ fontSize: 13 }}>{report.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ------------------------------------------------------------------ */
  /* Render report header                                                */
  /* ------------------------------------------------------------------ */

  const renderReportHeader = () => {
    if (!activeReport) return null;

    return (
      <div style={styles.reportHeader}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 650, marginBottom: 4 }}>
            {activeReport.label}
          </h1>
          {activeReport.subtitle && (
            <p style={{ fontSize: 13, color: "var(--ink-3)", margin: 0 }}>
              {activeReport.subtitle}
            </p>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn" onClick={handleExportExcel}>
            <FileSpreadsheet size={14} />
            Excel
          </button>
          <button className="btn" onClick={handleExportCSV}>
            <File size={14} />
            CSV
          </button>
          <button className="btn" onClick={handlePrint}>
            <Printer size={14} />
            Print
          </button>
        </div>
      </div>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Render meta badges                                                  */
  /* ------------------------------------------------------------------ */

  const renderMetaBadges = () => {
    if (!activeReport || !reportData?.meta) return null;

    return (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {Object.entries(reportData.meta).map(([key, value]) => (
          <Badge key={key} text={`${key}: ${value}`} variant="info" />
        ))}
      </div>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Render group by dropdown (for spend analysis)                       */
  /* ------------------------------------------------------------------ */

  const renderGroupByDropdown = () => {
    if (!activeReport || activeReport.id !== "spend_analysis") return null;

    return (
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)" }}>
          Group by:
        </label>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          style={{
            padding: "6px 10px",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius-sm)",
            background: "var(--panel)",
            color: "var(--ink)",
            fontSize: 13,
          }}
        >
          <option value="vendor">Vendor</option>
          <option value="category">Category</option>
          <option value="month">Month</option>
          <option value="item">Item</option>
        </select>
      </div>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Render report content                                               */
  /* ------------------------------------------------------------------ */

  const renderReportContent = () => {
    if (loadingReport) {
      return (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Loading show={true} text="Loading report data..." />
        </div>
      );
    }

    if (reportError) {
      return (
        <div style={{ padding: 60, textAlign: "center", color: "var(--bad)" }}>
          <AlertTriangle size={32} style={{ marginBottom: 12 }} />
          <p style={{ fontWeight: 600, fontSize: 15 }}>{reportError}</p>
        </div>
      );
    }

    if (!reportData || !reportData.rows || reportData.rows.length === 0) {
      return (
        <div style={{ padding: 60, textAlign: "center", color: "var(--ink-3)" }}>
          <FileText size={32} style={{ marginBottom: 12 }} />
          <p style={{ fontWeight: 600, fontSize: 15 }}>No data available</p>
          <p style={{ fontSize: 13 }}>Try adjusting the report parameters or select a different report.</p>
        </div>
      );
    }

    return (
      <DataTable
        columns={reportData.columns}
        rows={reportData.rows}
        emptyText="No data available"
      />
    );
  };

  /* ------------------------------------------------------------------ */
  /* Render active report                                                */
  /* ------------------------------------------------------------------ */

  const renderActiveReport = () => {
    if (!activeReport) {
      return (
        <div style={styles.emptyState}>
          <FileText size={48} style={{ color: "var(--ink-3)", marginBottom: 16 }} />
          <h2 style={{ fontSize: 18, fontWeight: 650, marginBottom: 8 }}>
            Select a Report
          </h2>
          <p style={{ fontSize: 13, color: "var(--ink-3)" }}>
            Choose a report from the sidebar to view and export data.
          </p>
        </div>
      );
    }

    return (
      <div style={styles.reportArea}>
        {renderReportHeader()}
        {renderMetaBadges()}
        {renderGroupByDropdown()}

        <div className="card">
          <div className="card-body flush">
            {renderReportContent()}
          </div>
        </div>
      </div>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Render                                                              */
  /* ------------------------------------------------------------------ */

  return (
    <div style={styles.container}>
      {renderSidebar()}
      {renderActiveReport()}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  container: {
    display: "flex",
    gap: 24,
    minHeight: "calc(100vh - 140px)",
  },
  sidebar: {
    width: 280,
    flexShrink: 0,
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
    boxShadow: "var(--shadow)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  sidebarHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "13px 16px",
    borderBottom: "1px solid var(--line-2)",
  },
  sidebarContent: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 0",
  },
  group: {
    marginBottom: 4,
  },
  groupHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 16px",
    cursor: "pointer",
    userSelect: "none",
  },
  groupItems: {
    paddingLeft: 8,
  },
  reportItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 16px",
    cursor: "pointer",
    borderRadius: "var(--radius-sm)",
    margin: "2px 8px",
    transition: "background 0.15s ease",
  },
  reportArea: {
    flex: 1,
    minWidth: 0,
  },
  reportHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    marginBottom: 20,
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
    background: "var(--panel)",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius)",
    boxShadow: "var(--shadow)",
  },
};
